/**
 * Enhanced GA Tracking – GTM-first dataLayer push
 * Events are consumed by GTM → GA4. Controlled by settings.enhancedTracking.enabled.
 */

import TagManager from 'react-gtm-module';

let _settings = null;
let _dataLayerName = 'dataLayer';

/**
 * Initialize tracking with site settings.
 * Call when settings are loaded (e.g. in TrackingProvider).
 * When enhancedTracking.enabled, pushes to the GTM config with dataLayerName 'palsDataLayer'.
 * @param {Object} settings - Site settings (must include enhancedTracking, domain)
 */
export const init = (settings) => {
  if (!settings) {
    return;
  }
  _settings = settings;
  const gtmConfigs = Array.isArray(settings.gtm) ? settings.gtm : (settings.gtm ? [settings.gtm] : []);
  const palsGtm = gtmConfigs.find((g) => g.dataLayerName === 'palsDataLayer');
  const defaultGtm = gtmConfigs[0];

  if (settings.enhancedTracking?.enabled && palsGtm) {
    _dataLayerName = palsGtm.dataLayerName;
  } else {
    _dataLayerName = (defaultGtm?.dataLayerName) || 'dataLayer';
  }
}

/**
 * Whether enhanced tracking is enabled for this site.
 * @returns {boolean}
 */
export function shouldTrack() {
  return Boolean(_settings?.enhancedTracking?.enabled);
}

/**
 * Push an event to the dataLayer. No-op if tracking is disabled.
 * @param {string} eventName - GTM custom event name
 * @param {Object} params - Event parameters
 */
const push = (eventName, params = {}) => {
  if (!shouldTrack()) {
    return;
  }
  const payload = {event: eventName, ...params};
  TagManager.dataLayer({
    dataLayer: payload,
    dataLayerName: _dataLayerName
  });
};

/**
 * Build filter params for pals_filters_apply from filter state.
 * @param {Object} state - Filter state (selectedStates, selectedPlanTypes, selectedDynamicFilters)
 * @param {Object} settings - Site settings
 * @returns {Object} Params object
 */
const toPipeDelimited = (arr, getLabel = (x) => x) => {
  if (!Array.isArray(arr)) {
    return '';
  }
  return arr.map((x) => (x && typeof x === 'object' ? getLabel(x) : x)).filter(Boolean).join('|');
};

export const buildFiltersApplyParams = (state, settings) => {
  const payerRaw = state.selectedDynamicFilters?.payer ?? state.selectedDynamicFilters?.['pbmClient'];
  const payerFilter = Array.isArray(payerRaw) ? toPipeDelimited(payerRaw, (x) => x.label ?? x.name ?? x) : (payerRaw || '');
  const planTypeFilter = toPipeDelimited(state.selectedPlanTypes, (x) => x?.label ?? x?.name ?? x);
  const indicationRaw = state.selectedDynamicFilters?.indication;
  const indicationFilter = Array.isArray(indicationRaw) ? toPipeDelimited(indicationRaw, (x) => x.label ?? x.name ?? x) : (indicationRaw || '');
  const stateFilter = toPipeDelimited(state.selectedStates, (x) => x?.label ?? x?.name ?? x?.id ?? x);

  return {
    payer_filter: String(payerFilter),
    plan_type_filter: String(planTypeFilter),
    indication_filter: String(indicationFilter),
    state_filter: String(stateFilter),
    site_id: settings?.domain || ''
  };
};

/**
 * Event 1: pals_filters_apply – when results refresh due to filter changes
 */
export const filtersApply = (filterParams, resultsCount, siteId) => {
  push('pals_filters_apply', {
    ...filterParams,
    results_count: resultsCount,
    site_id: siteId || _settings?.domain || ''
  });
};

/**
 * Event 2: pals_result_click – click within a results table row
 * @param {string} clickType - coverage_document | pa_form | coverage_summary | detailed_brochure
 * @param {Object} rowData - Row data for params
 */
export const resultClick = (clickType, rowData) => {
  const params = {
    click_type: clickType,
    payer: rowData?.payer ?? '',
    plan_type: rowData?.planType ?? '',
    indication: rowData?.indication ?? '',
    row_states: Array.isArray(rowData?.states) ? rowData.states.join('|') : (rowData?.states ?? ''),
    benefit_type: rowData?.benefitType ?? rowData?.benefit ?? '',
    covered_status: rowData?.covered ?? rowData?.Covered ?? '',
    prior_auth_status: rowData?.priorAuth ?? rowData?.['Prior Authorization'] ?? '',
    last_review_date: rowData?.lastReviewDate ?? rowData?.['Last Day Reviewed'] ?? '',
    site_id: _settings?.domain || ''
  };
  if (rowData?.policyId) {
    params.policy_id = rowData.policyId;
  }
  push('pals_result_click', params);
};

/**
 * Event 3: pals_simple_brochure_click – top-level brochure button click
 */
export const simpleBrochureClick = (params = {}) => {
  push('pals_simple_brochure_click', {
    site_id: _settings?.domain || '',
    payer_filter: params.payer_filter ?? '',
    plan_type_filter: params.plan_type_filter ?? '',
    indication_filter: params.indication_filter ?? '',
    state_filter: params.state_filter ?? ''
  });
};

/**
 * Event 4: pals_request_rep_click – rep/request/contact links
 */
export const requestRepClick = (params) => {
  push('pals_request_rep_click', {
    site_id: _settings?.domain || '',
    link_name: params.link_name ?? '',
    link_url: params.link_url ?? '',
    payer_filter: params.payer_filter ?? '',
    plan_type_filter: params.plan_type_filter ?? '',
    indication_filter: params.indication_filter ?? '',
    state_filter: params.state_filter ?? ''
  });
};

/**
 * Event 5: pals_payer_link_modal – ExitModal outcome (leaves | stays)
 */
export const externalLinkExitModal = (outcome) => {
  push('pals_payer_link_modal', {
    outcome: outcome === 'leaves' || outcome === 'stays' ? outcome : 'stays',
    site_id: _settings?.domain || ''
  });
};
